import { useEffect, useMemo, useState, type KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
// import Card from "../../../shared/components/Card";
// import Button from "../../../shared/components/Button";
// import MyCourses from "../components/MyCourses";
import PrimaryIcon from "../../../shared/components/PrimaryIcon";
import SecondaryIcon from "../../../shared/components/SecondaryIcon";
import Icons from "../../../shared/constants/icon.constant";
// import Select from "../../../shared/components/Select";
// import { XAxis, LineChart, Line, YAxis } from "recharts";
// import {
//   ChartContainer,
//   ChartTooltip,
//   ChartTooltipContent,
// } from "@/shared/components/ui/chart";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shared/components/ui/avatar";
import { User, Plus } from "lucide-react";
import ComingSoon from "@/shared/components/ComingSoon";
// import { Heart, MessageCircle } from "lucide-react";
// import { Button } from "@/shared/components/ui/button";
import { Skeleton } from "@/shared/components/ui/skeleton";
import {
  computeWalletSummary,
  useUserBalances,
  useUpgradeMembershipBanner,
} from "../services";
import type { IUserBalance } from "../interfaces/balance.interface";
import { useTransactionHistory } from "../services/transactions";
import { format } from "date-fns";
import { useProfile } from "@/features/profile/services/profile";
import BannerCarousel from "../components/BannerCarousel";
import { ComplianceModal } from "@/features/kyc/components/ComplianceModal";
import { hasCompletedCompliance } from "@/features/kyc/services/compliance";

const fallbackCurrencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
});

// function StatRow({
//   label,
//   value,
//   icon,
// }: {
//   label: string;
//   value: string;
//   icon: JSX.Element;
// }) {
//   return (
//     <div className="flex items-center justify-between py-1">
//       <div className="flex items-center gap-2 text-sm text-white/60">
//         <SecondaryIcon icon={icon} />
//         {label}
//       </div>
//       <div className="text-sm">{value}</div>
//     </div>
//   );
// }

export default function Dashboard() {
  const {
    data: balancesData,
    isLoading: isLoadingBalances,
    error: balancesError,
  } = useUserBalances();

  const { data: upgradeBanners, isLoading: isLoadingUpgradeBanner } =
    useUpgradeMembershipBanner();
  const upgradeBanner = upgradeBanners?.[0];

  const navigate = useNavigate();
  const [isComingSoonOpen, setIsComingSoonOpen] = useState(false);

  // Get user profile
  const { data: profileData, isLoading: isLoadingProfile } = useProfile();

  // Check if compliance modal should be shown
  const shouldShowComplianceModal = useMemo(() => {
    if (!profileData || isLoadingProfile) return false;
    return !hasCompletedCompliance();
  }, [profileData, isLoadingProfile]);

  const [isComplianceModalOpen, setIsComplianceModalOpen] = useState(false);

  // Open compliance modal when profile data is loaded and compliance is needed
  useEffect(() => {
    if (shouldShowComplianceModal) {
      setIsComplianceModalOpen(true);
    }
  }, [shouldShowComplianceModal]);

  // Get current month and year for transaction history
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1; // getMonth() returns 0-11
  const currentYear = currentDate.getFullYear();

  const {
    data: transactionsData,
    isLoading: isLoadingTransactions,
    error: transactionsError,
  } = useTransactionHistory({
    month: currentMonth,
    year: currentYear,
  });

  const balances: IUserBalance[] = useMemo(
    () => balancesData?.balances ?? [],
    [balancesData]
  );
  const primaryBalance = balances[0];

  const baseCurrency =
    primaryBalance?.currency ||
    balancesData?.fiatBalances?.[0]?.name ||
    balancesData?.cryptoBalances?.[0]?.name ||
    "USD";

  const currencyFormatter = useMemo(() => {
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: baseCurrency,
        maximumFractionDigits: 2,
      });
    } catch {
      return fallbackCurrencyFormatter;
    }
  }, [baseCurrency]);

  const formatCurrencyValue = (value: number) =>
    currencyFormatter.format(Number.isFinite(value) ? value : 0);

  const {
    totalWalletValue,
    totalFiatValue,
    totalCryptoValue,
    hasDetailedBalances,
  } = useMemo(() => computeWalletSummary(balancesData), [balancesData]);

  const showAggregatedView = hasDetailedBalances;
  const walletAmountDisplay = showAggregatedView
    ? formatCurrencyValue(totalWalletValue)
    : (primaryBalance?.balance ?? "0.00");
  const walletCurrencyLabel = primaryBalance?.currency ?? baseCurrency;
  const cryptoAmountDisplay = showAggregatedView
    ? formatCurrencyValue(totalCryptoValue)
    : "--";
  const fiatAmountDisplay = showAggregatedView
    ? formatCurrencyValue(totalFiatValue)
    : "--";

  const handleWalletCardClick = () => {
    navigate("/assets/wallet");
  };

  const handleWalletCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleWalletCardClick();
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
      {/* Main content column */}
      <div className="col-span-12 md:col-span-9 flex flex-col gap-4">
        {/* Welcome header */}
        <p className="text-sm text-white/60">
          Welcome to Nommia, your hub for trading, learning, and growth! Explore
          markets, master trading skills, and unlock new opportunities today.
        </p>

        {/* Profile card with API integration */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="col-span-12 rounded-xl text-white flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Avatar className="size-12 bg-yellow-400 border border-white/10">
                <AvatarImage
                  src={
                    profileData?.profileImage ||
                    "https://png.pngtree.com/png-clipart/20231019/original/pngtree-user-profile-avatar-png-image_13369988.png"
                  }
                  alt="profile"
                  className="object-cover"
                />
                <AvatarFallback className="bg-yellow-400 p-2">
                  <User className="size-32" />
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                {isLoadingProfile ? (
                  <Skeleton className="h-4 w-24 bg-white/10" />
                ) : (
                  <span className="text-sm">
                    Hi, {profileData?.firstName || "User"}!
                  </span>
                )}
                <span className="text-[11px] text-white/60">
                  {format(new Date(), "dd MMMM yyyy")}
                </span>
              </div>
            </div>
            {isLoadingUpgradeBanner ? (
              <Skeleton className="h-20 w-48 bg-white/10 rounded" />
            ) : (
              upgradeBanner && (
                <a
                  href={upgradeBanner.linkUrl || undefined}
                  target={upgradeBanner.linkUrl ? "_blank" : undefined}
                  rel={
                    upgradeBanner.linkUrl ? "noopener noreferrer" : undefined
                  }
                  className={`block ${
                    upgradeBanner.linkUrl
                      ? ""
                      : "cursor-default pointer-events-none"
                  }`}>
                  <img
                    src={upgradeBanner.imageUrl}
                    alt={upgradeBanner.title || "Upgrade membership banner"}
                    className="h-20 w-auto object-contain rounded"
                  />
                </a>
              )
            )}
          </div>
        </div>

        {/* Balances (only Wallet overview shown) */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {/* Wallet */}
          <div
            role="button"
            tabIndex={0}
            aria-label="View wallet details"
            onClick={handleWalletCardClick}
            onKeyDown={handleWalletCardKeyDown}
            className="col-span-12 md:col-span-4 rounded-xl bg-[#1F242A] px-2 py-2 flex flex-col justify-between cursor-pointer transition hover:bg-[#242A30] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0F13]">
            <div className="flex items-start justify-between w-full">
              <div className="px-2 pt-2 w-full">
                <div className="flex items-center justify-between flex-1 w-full">
                  <p className="text-xs text-white/70">Wallet</p>
                  <img
                    src="/assets/icons/top-right-icon.svg"
                    alt="wallet overview"
                  />
                </div>
                <div className="mt-1 min-h-[32px]">
                  {isLoadingBalances ? (
                    <Skeleton className="h-6 w-24 bg-white/10" />
                  ) : balancesError ? (
                    <p className="text-xs text-red-400">
                      {balancesError?.message ?? "Failed to load balances."}
                    </p>
                  ) : !showAggregatedView && !primaryBalance ? (
                    <span className="text-sm text-white/60">
                      No balances yet
                    </span>
                  ) : (
                    <>
                      <span className="text-lg">{walletAmountDisplay}</span>
                      {walletCurrencyLabel ? (
                        <span className="text-xs text-white/60 ml-1">
                          {walletCurrencyLabel}
                        </span>
                      ) : null}
                    </>
                  )}
                </div>
                <p className="text-xs text-white/60">Total Balance</p>
              </div>
            </div>
            <div className="mt-2 flex gap-2 w-full">
              <div className="flex flex-1 w-full items-center gap-2 bg-[#121717] rounded-lg px-3 py-2">
                <PrimaryIcon icon={Icons.Coin({ width: 16, height: 16 })} />
                <div>
                  <p className="text-[11px]">Crypto Wallet</p>
                  {isLoadingBalances ? (
                    <Skeleton className="h-4 w-16 bg-white/10" />
                  ) : (
                    <p className="text-[11px] text-white/70">
                      {balancesError ? "--" : cryptoAmountDisplay}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-1 w-full items-center gap-2 bg-[#121717] rounded-lg px-3 py-2">
                <SecondaryIcon icon={Icons.Wallet({ width: 16, height: 16 })} />
                <div>
                  <p className="text-[11px]">Fiat Account</p>
                  {isLoadingBalances ? (
                    <Skeleton className="h-4 w-16 bg-white/10" />
                  ) : (
                    <p className="text-[11px] text-white/70">
                      {balancesError ? "--" : fiatAmountDisplay}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cards Section */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-white/60">Your Cards</p>
              <button
                onClick={() => setIsComingSoonOpen(true)}
                className="text-xs text-primary hover:underline">
                See All
              </button>
            </div>
            <div
              onClick={() => setIsComingSoonOpen(true)}
              className="bg-[#1F242A] rounded-xl p-4 flex flex-col gap-4 cursor-pointer hover:bg-[#242A30] transition h-40 items-center justify-center border border-dashed border-white/10">
              <div className="h-12 w-12 rounded-full bg-white/5 flex items-center justify-center">
                <Plus className="h-6 w-6 text-white/40" />
              </div>
              <p className="text-sm text-white/40">Add New Card</p>
            </div>
          </div>
        </div>

        {/* Coming Soon Modal */}
        {isComingSoonOpen && (
          <ComingSoon onClose={() => setIsComingSoonOpen(false)} />
        )}

        {/* Education Stats section is hidden */}

        {/* My Courses - Hidden until API is ready */}
        {/* <div className="flex flex-col">
          <h2 className="text-lg mb-3">My Courses</h2>
          <MyCourses />
        </div> */}

        {/* Trending pairs - Hidden until API is ready */}
        {/* <div className="flex items-center justify-between">
          <h2 className="text-lg">Trending Pairs</h2>
          <Select
            value="24h"
            options={[
              { label: "24 Hour", value: "24h" },
              { label: "7 Days", value: "7d" },
              { label: "30 Days", value: "30d" },
            ]}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-4">
          {["ETH/USDT", "ETH/USDT", "BTC/USDT"].map((pair, i) => (
            <Card
              key={pair + i}
              className="col-span-12 sm:col-span-6 md:col-span-4 !p-0">
              <div className="flex items-center justify-between w-full p-4 pb-0">
                <div className="flex items-center gap-3">
                  <img
                    src="/assets/icons/bitcoin-icon.png"
                    alt={pair}
                    className="size-8"
                  />
                  <div>
                    <div className="text-xs text-label-dark">Pair</div>
                    <div className="font-semibold tracking-wide">{pair}</div>
                  </div>
                </div>

                <img
                  src="/assets/icons/top-right-icon.svg"
                  alt={pair}
                  className="size-4 self-start"
                />
              </div>

              <div className="flex items-center justify-between mt-4 text-sm p-4 pt-0">
                <div>24h Change</div>
                <div
                  className={`font-semibold ${i === 2 ? "text-red-400" : "text-green-400"}`}>
                  {i === 2 ? "-3.56%" : "+3.56%"}
                </div>
              </div>

              <div className="mt-3 w-full">
                <ChartContainer
                  config={{
                    base: { label: "Base", color: "rgba(255,255,255,0.18)" },
                    change: {
                      label: "24h Change",
                      color: i === 2 ? "#ef4444" : "#22c55e",
                    },
                    marker: { label: "Marker", color: "#F59E0B" },
                  }}
                  className="h-full w-full">
                  <LineChart
                    data={[
                      { t: "12am", v: 27.1, highlight: null },
                      { t: "3am", v: 26.9, highlight: null },
                      { t: "6am", v: 27.2, highlight: null },
                      { t: "9am", v: 28038, highlight: 28038 },
                      { t: "12pm", v: 27700, highlight: 27700 },
                      { t: "3pm", v: 27100, highlight: null },
                      { t: "6pm", v: 27800, highlight: null },
                      { t: "9pm", v: 27400, highlight: null },
                    ]}>
                    <XAxis
                      dataKey="t"
                      tick={{ fill: "#9CA3AF", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis hide />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Line
                      type="monotone"
                      dataKey="v"
                      stroke="var(--color-base)"
                      strokeWidth={2.5}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="highlight"
                      stroke="var(--color-change)"
                      strokeWidth={3}
                      dot={{
                        r: 6,
                        fill: "var(--color-marker)",
                        stroke: "#FFFFFF",
                        strokeWidth: 3,
                      }}
                    />
                  </LineChart>
                </ChartContainer>
              </div>
            </Card>
          ))}
        </div> */}

        {/* Market performance - Hidden until API is ready */}
        {/* <h2 className="text-lg">Market Performance</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-4">
          <Card className="col-span-12 sm:col-span-6 md:col-span-4 flex flex-col">
            <div className="flex justify-between w-full">
              <div className="flex gap-2">
                <img
                  src="/assets/icons/up-icon.svg"
                  alt="Top Gainer"
                  className="w-6 h-6"
                />
                <span>Top Gainer</span>
              </div>
              <img src="/assets/icons/top-right-icon.svg" />
            </div>
            <div className="py-4 items-center flex justify-center">
              <img
                src="/assets/icons/eth-usdt-icon.png"
                alt="Top Gainer"
                className="size-14"
              />
            </div>
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <div className="text-sm">ETH/USDT</div>
              </div>
              <div className="text-green-400 text-sm">+8.67%</div>
            </div>
          </Card>
          <Card className="col-span-12 sm:col-span-6 md:col-span-4 flex flex-col">
            <div className="flex justify-between w-full">
              <div className="flex gap-2">
                <img
                  src="/assets/icons/down-icon.svg"
                  alt="Top Loser"
                  className="w-6 h-6"
                />
                <span>Top Loser</span>
              </div>
              <img src="/assets/icons/top-right-icon.svg" />
            </div>
            <div className="py-4 items-center flex justify-center">
              <img
                src="/assets/icons/doge-icon.png"
                alt="Top Loser"
                className="size-14"
              />
            </div>

            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <div className="text-sm">DOGE/USDT</div>
              </div>
              <div className="text-red-400 text-sm">-2.13%</div>
            </div>
          </Card>
          <Card className="col-span-12 sm:col-span-6 md:col-span-4 flex flex-col">
            <div className="flex justify-between w-full">
              <div className="flex gap-2">
                <img
                  src="/assets/icons/box-icon.svg"
                  alt="Volume Leader"
                  className="w-6 h-6"
                />
                <span>Volume Leader</span>
              </div>
              <img src="/assets/icons/top-right-icon.svg" />
            </div>
            <div className="py-4 items-center flex justify-center">
              <img
                src="/assets/icons/solana-icon.png"
                alt="Volume Leader"
                className="size-14"
              />
            </div>
            <Card className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <div className="text-sm">SOL/USDT</div>
              </div>
              <div className="text-yellow-300 text-sm">$11,383,123</div>
          </Card>
        </div> */}
      </div>

      {/* Right sidebar (Recent Transactions only) */}
      <div className="col-span-12 md:col-span-3 flex flex-col gap-4">
        <BannerCarousel />

        {/* Refer & Earn banner - Hidden until API is ready */}
        {/* <div className="w-full relative rounded-lg aspect-video">
          <img
            src="/0a44ecd0af80a171cd5a1383572cdd4a.png"
            alt="Refer & Earn"
            className="w-full h-full object-cover rounded-lg aspect-video"
          />
          <div className="bg-black/40 h-full absolute top-0 left-0 right-0 w-full inset-0 flex flex-col items-start justify-between p-4">
            <div className="flex flex-col text-[#FF3282]">
              <span>Invite Friends, Earn Rewards</span>
            </div>
            <div className="text-xs">
              Earn up to $50 for each friend you invite by 15 February
            </div>
            <Button className="self-start !bg-[#FF3282] text-white rounded-full">
              Refer & Earn
            </Button>
          </div>
        </div> */}

        {/* Recent Transactions */}
        <div className="flex flex-col gap-3">
          <span className="text-sm font-medium text-white">
            Recent Transactions
          </span>
          <div className="rounded-2xl bg-[#1F242A] px-3">
            {isLoadingTransactions ? (
              <div className="py-8 flex justify-center">
                <Skeleton className="h-6 w-32 bg-white/10" />
              </div>
            ) : transactionsError ? (
              <div className="py-8 text-center text-xs text-red-400">
                {transactionsError?.message ?? "Failed to load transactions"}
              </div>
            ) : !transactionsData || transactionsData.length === 0 ? (
              <div className="py-8 text-center text-xs text-white/60">
                No transactions yet
              </div>
            ) : (
              <div className="divide-y divide-white/10">
                {transactionsData.map((tx) => {
                  // Determine if it's a deposit or withdrawal
                  const isDeposit = [
                    "sepaDeposit",
                    "cryptoDeposit",
                    "fiatDeposit",
                    "exchange",
                  ].includes(tx.type);
                  const positive = isDeposit;

                  // Format amount
                  const amount = Math.abs(tx.amount ?? 0);
                  const amountLabel = Number.isFinite(amount)
                    ? amount.toString()
                    : "0";

                  // Get transaction title and capitalize it
                  const rawTitle =
                    tx.transferTitle ||
                    tx.type.replace(/([A-Z])/g, " $1").trim();
                  const title = rawTitle
                    .split(" ")
                    .map(
                      (word) =>
                        word.charAt(0).toUpperCase() +
                        word.slice(1).toLowerCase()
                    )
                    .join(" ");

                  const directionLabel = positive ? "Incoming" : "Outgoing";
                  const combinedTitle = `${directionLabel} ${title}`;

                  // Format date
                  let displayDate = "";
                  try {
                    displayDate = format(
                      new Date(tx.createdAt),
                      "dd MMMM yyyy HH:mm"
                    );
                  } catch {
                    displayDate = "";
                  }

                  // Get currency logo from CDN
                  const getCurrencyLogo = (currency: string) => {
                    const curr = currency.toUpperCase();
                    switch (curr) {
                      case "BTC":
                        return "https://cryptologos.cc/logos/bitcoin-btc-logo.svg";
                      case "ETH":
                        return "https://cryptologos.cc/logos/ethereum-eth-logo.svg";
                      case "USDT":
                        return "https://cryptologos.cc/logos/tether-usdt-logo.svg";
                      case "USDC":
                        return "https://cryptologos.cc/logos/usd-coin-usdc-logo.svg";
                      case "BNB":
                        return "https://cryptologos.cc/logos/bnb-bnb-logo.svg";
                      case "SOL":
                        return "https://cryptologos.cc/logos/solana-sol-logo.svg";
                      case "XRP":
                        return "https://cryptologos.cc/logos/xrp-xrp-logo.svg";
                      case "ADA":
                        return "https://cryptologos.cc/logos/cardano-ada-logo.svg";
                      case "DOGE":
                        return "https://cryptologos.cc/logos/dogecoin-doge-logo.svg";
                      case "EUR":
                        return "https://hatscripts.github.io/circle-flags/flags/eu.svg";
                      case "USD":
                        return "https://hatscripts.github.io/circle-flags/flags/us.svg";
                      case "GBP":
                        return "https://hatscripts.github.io/circle-flags/flags/gb.svg";
                      default:
                        return "https://cryptologos.cc/logos/usd-coin-usdc-logo.svg";
                    }
                  };

                  return (
                    <div
                      key={tx.id}
                      className="flex items-start justify-between gap-3 py-3">
                      <div className="flex items-center gap-2">
                        <div className="size-8 rounded-full overflow-hidden bg-white/5 flex items-center justify-center p-1">
                          <img
                            src={getCurrencyLogo(tx.currency)}
                            alt={tx.currency}
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <div className="flex flex-col">
                          <span className="font-medium text-[12px] tracking-wide">
                            {combinedTitle}
                          </span>
                          {displayDate && (
                            <span className="text-[10px] text-white/60">
                              {displayDate}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end text-right">
                        <span className="text-[10px] text-white/60 uppercase">
                          {tx.currency}
                        </span>
                        <span
                          className={`text-sm font-semibold ${positive ? "text-green-400" : "text-red-400"}`}>
                          {positive ? "+" : "-"}
                          {amountLabel}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Top Discussions - Hidden until API is ready */}
      </div>

      {/* Compliance Modal */}
      <ComplianceModal
        isOpen={isComplianceModalOpen}
        onClose={() => setIsComplianceModalOpen(false)}
      />
    </div>
  );
}
